// frontend/src/hooks/useFunVideos.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dbSelect, invokeFunction, dbDelete, dbUpdate } from '../lib/api';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export interface FunVideo {
  id: string;
  prompt: string;
  name: string;
  video_url: string | null;
  storage_path: string | null;
  xai_request_id: string | null;
  status: 'pending' | 'generating' | 'ready' | 'failed';
  error_message: string | null;
  duration: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Build the public Storage URL for a video, or fall back to the xAI URL.
// The fun-videos bucket is public, so we can use the public URL directly.
export function getVideoSrc(video: FunVideo): string | null {
  if (video.storage_path) {
    return `${supabaseUrl}/storage/v1/object/public/fun-videos/${video.storage_path}`;
  }
  return video.video_url || null;
}

// Hook for fetching ready videos (used in ConversationPage)
// Videos are public (RLS allows anyone to read ready videos), so no session needed.
export function useReadyVideos() {
  const [videos, setVideos] = useState<FunVideo[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await dbSelect<FunVideo[]>('fun_videos', {
        select: 'id,name,video_url,storage_path',
        filters: { status: 'eq.ready' },
      });

      if (!error && data) {
        // Only include videos that have at least one playable source
        setVideos(data.filter(v => v.storage_path || v.video_url));
      }
    } catch (err) {
      console.error('Failed to fetch ready videos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Deterministically assign a video to a message ID
  // Uses a simple hash to consistently map message IDs to video indices
  const getVideoForMessage = useCallback(
    (messageId: string): FunVideo | null => {
      if (videos.length === 0) return null;

      // Simple hash: sum char codes and modulo by video count
      let hash = 0;
      for (let i = 0; i < messageId.length; i++) {
        hash = ((hash << 5) - hash + messageId.charCodeAt(i)) | 0;
      }
      const index = Math.abs(hash) % videos.length;
      return videos[index];
    },
    [videos]
  );

  return { videos, loading, getVideoForMessage, refetch: fetchVideos };
}

// Hook for admin video management
export function useAdminVideos() {
  const { session } = useAuth();
  const [videos, setVideos] = useState<FunVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pollingIds, setPollingIds] = useState<Set<string>>(new Set());
  // Tracks the last time each video was successfully confirmed as still generating
  const [lastPolledAt, setLastPolledAt] = useState<Map<string, Date>>(new Map());

  // Use a ref for session so polling intervals always have the latest token
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Track all active intervals so we can clean them up
  const intervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  // Clean up all intervals on unmount
  useEffect(() => {
    return () => {
      intervalsRef.current.forEach((interval) => clearInterval(interval));
      intervalsRef.current.clear();
    };
  }, []);

  const fetchAllVideos = useCallback(async () => {
    if (!session) return;

    setLoading(true);
    try {
      const { data, error } = await dbSelect<FunVideo[]>('fun_videos', {
        token: session.access_token,
        select: '*',
        order: 'created_at.desc',
      });

      if (!error && data) {
        setVideos(data);
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchAllVideos();
  }, [fetchAllVideos]);

  // Generate a new video
  const generateVideo = useCallback(
    async (prompt: string, name?: string): Promise<FunVideo | null> => {
      if (!session) return null;

      setGenerating(true);
      try {
        const { data, error } = await invokeFunction<FunVideo>('generate-video', {
          prompt,
          name: name || undefined,
          duration: 15,
        }, { token: session.access_token });

        if (error) {
          console.error('Generate video error:', error);
          return null;
        }

        if (data) {
          setVideos(prev => [data, ...prev]);
          // Start polling if generating
          if (data.status === 'generating') {
            startPolling(data.id);
          }
          return data;
        }
        return null;
      } finally {
        setGenerating(false);
      }
    },
    [session]
  );

  // Auto-generate a name for a prompt
  const generateName = useCallback(
    async (prompt: string): Promise<string | null> => {
      if (!session) return null;

      try {
        const { data, error } = await invokeFunction<{ name: string }>('generate-video', {
          action: 'generate-name',
          prompt,
        }, { token: session.access_token });

        if (!error && data) {
          return data.name;
        }
        return null;
      } catch {
        return null;
      }
    },
    [session]
  );

  // Poll a specific video for status updates
  // Returns { authError: true } sentinel when token is expired
  const pollVideo = useCallback(
    async (videoId: string): Promise<FunVideo | null | { authError: true }> => {
      const currentSession = sessionRef.current;
      if (!currentSession) return { authError: true };

      try {
        const { data, error } = await invokeFunction<FunVideo>('generate-video', {
          action: 'poll',
          videoId,
        }, { token: currentSession.access_token });

        if (error) {
          const errorMsg = error.message || '';
          // Stop polling on auth errors - token expired or unauthorized
          if (errorMsg.includes('401') || errorMsg.includes('Unauthorized') || errorMsg.includes('JWT expired')) {
            console.warn('Polling stopped: auth token expired for video', videoId);
            return { authError: true };
          }
          return null;
        }

        if (data) {
          setVideos(prev =>
            prev.map(v => (v.id === videoId ? data : v))
          );
          // Update last polled timestamp on every successful poll response
          setLastPolledAt(prev => {
            const next = new Map(prev);
            next.set(videoId, new Date());
            return next;
          });
          return data;
        }
        return null;
      } catch {
        return null;
      }
    },
    []
  );

  // Helper to clean up polling for a video
  const stopPolling = useCallback((videoId: string) => {
    const interval = intervalsRef.current.get(videoId);
    if (interval) {
      clearInterval(interval);
      intervalsRef.current.delete(videoId);
    }
    setPollingIds(prev => {
      const next = new Set(prev);
      next.delete(videoId);
      return next;
    });
    // Clean up lastPolledAt entry when polling stops
    setLastPolledAt(prev => {
      const next = new Map(prev);
      next.delete(videoId);
      return next;
    });
  }, []);

  // Start polling interval for a video
  const startPolling = useCallback(
    (videoId: string) => {
      if (pollingIds.has(videoId)) return;

      setPollingIds(prev => new Set(prev).add(videoId));

      const interval = setInterval(async () => {
        const result = await pollVideo(videoId);

        // Stop on auth error (expired token)
        if (result && 'authError' in result) {
          stopPolling(videoId);
          return;
        }

        // Stop on terminal status
        if (result && 'status' in result && (result.status === 'ready' || result.status === 'failed')) {
          stopPolling(videoId);
        }
      }, 5000); // Poll every 5 seconds

      intervalsRef.current.set(videoId, interval);
    },
    [pollVideo, pollingIds, stopPolling]
  );

  // Update a video's name
  const updateVideoName = useCallback(
    async (videoId: string, newName: string): Promise<boolean> => {
      if (!session) return false;

      const { error } = await dbUpdate(
        'fun_videos',
        { name: newName },
        { id: `eq.${videoId}` },
        { token: session.access_token, returning: false }
      );

      if (!error) {
        setVideos(prev =>
          prev.map(v => (v.id === videoId ? { ...v, name: newName } : v))
        );
        return true;
      }
      return false;
    },
    [session]
  );

  // Delete a video
  const deleteVideo = useCallback(
    async (videoId: string): Promise<boolean> => {
      if (!session) return false;

      const { error } = await dbDelete('fun_videos', { id: `eq.${videoId}` }, {
        token: session.access_token,
      });

      if (!error) {
        setVideos(prev => prev.filter(v => v.id !== videoId));
        return true;
      }
      return false;
    },
    [session]
  );

  // Resume polling for any videos that are still generating
  useEffect(() => {
    videos.forEach(video => {
      if (video.status === 'generating' && !pollingIds.has(video.id)) {
        startPolling(video.id);
      }
    });
  }, [videos, pollingIds, startPolling]);

  return {
    videos,
    loading,
    generating,
    pollingIds,
    lastPolledAt,
    generateVideo,
    generateName,
    pollVideo,
    updateVideoName,
    deleteVideo,
    refetch: fetchAllVideos,
  };
}